// @ts-nocheck
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';

import { FHAutocomplete, FHMUIDataTable, FHSelect } from '../../../base';
import { getBuildInvoiceTableColumns, getBuildInvoiceTableOptions } from './config';
import { claimsInvoiceActions } from '../../../../store';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_WHITE } from '../../../../utils/colors';
import SummaryBox from './SummaryBox';
import { getCurrentCTDateTime } from '../../../../helpers';
import { API_DATE_FORMAT, BackgroundTypes } from '../../../../constants';
import { ClinicalStatus } from '../../../../admin/constants/general';

import type { RootState } from '../../../../store';
import type { IInvoiceHistoryTableRowData } from './InvoiceHistoryTableRow';
import type { ChangeEvent, Dispatch, HTMLAttributes, SetStateAction } from 'react';
import type { IClient, IOption, SelectValueType } from '../../../../utils/types';

interface IInvoiceBuilderProps {
  selectedPayee: number | null;
  selectedClient: IOption | null;
  isSelectedAll: boolean;
  dueInDays: number | null;
  totalDue: number;
  discount: string | null;
  totalNumClaimSelected: number;
  transformedTableData: IInvoiceHistoryTableRowData[];
  setDiscount: Dispatch<SetStateAction<string | null>>;
  setTransformedTableData: Dispatch<SetStateAction<IInvoiceHistoryTableRowData[]>>;
  setSelectedClient: Dispatch<SetStateAction<IOption | null>>;
  setSelectedPayee: Dispatch<SetStateAction<number | null>>;
  setIsSelectedAll: Dispatch<SetStateAction<boolean>>;
  onTabChange: Dispatch<SetStateAction<number>>;
}

function InvoiceBuilder({
  selectedClient,
  selectedPayee,
  isSelectedAll,
  dueInDays,
  totalDue,
  discount,
  transformedTableData,
  totalNumClaimSelected,
  setTransformedTableData,
  setIsSelectedAll,
  setDiscount,
  setSelectedClient,
  setSelectedPayee,
  onTabChange,
}: IInvoiceBuilderProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { allClientsList: clients = [], loading: clientsDataLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { invoicePreview } = useSelector((state: RootState) => state.claimsInvoice);

  const handleSelectAll = (selectAll: boolean) => {
    setIsSelectedAll(selectAll);
    const updatedData = transformedTableData.map((row) => ({
      ...row,
      selected: !row.invoice_id && row.is_invoiceable ? selectAll : false,
    }));
    setTransformedTableData(updatedData);
    dispatch(claimsInvoiceActions.updateState({ key: 'invoicePreview', data: null }));
  };

  const handleRowSelect = (selected: boolean, claimId: number) => {
    if (!selected) setIsSelectedAll(false);
    const updatedData = transformedTableData.map((row) => {
      if (row.id === claimId) {
        return {
          ...row,
          selected,
        };
      }
      return row;
    });
    const selectableRowCount =
      transformedTableData.filter((row) => !row.invoice_id && row.is_invoiceable).length ?? 0;
    const currentSelectedRowCount = updatedData.filter((row) => row.selected).length ?? 0;
    if (currentSelectedRowCount === selectableRowCount) {
      setIsSelectedAll(true);
    } else {
      setIsSelectedAll(false);
    }
    setTransformedTableData(updatedData);
    dispatch(claimsInvoiceActions.updateState({ key: 'invoicePreview', data: null }));
  };

  const handleCreateInvoicePreview = async () => {
    if (!dueInDays || !selectedPayee) return;
    if (invoicePreview) {
      onTabChange(1);
      return;
    }
    const data = {
      client_id: selectedClient?.id,
      discount: discount ?? null,
      claim_ids: transformedTableData.filter((row) => row.selected).map((row) => row.id),
      date_due: getCurrentCTDateTime().add(dueInDays, 'days').format(API_DATE_FORMAT),
      payee_provider_id: selectedPayee,
    };
    const response = await dispatch(
      claimsInvoiceActions.createInvoicePreview({ getAccessTokenSilently, data }),
    );
    if (!response.error) {
      dispatch(claimsInvoiceActions.updateState({ key: 'invoicePreview', data: response.payload }));
      onTabChange(1);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex' }}>
        <Box textAlign="left" mb={3} mr={3}>
          <Typography fontWeight={700} mb={1} color={FORTA_BLUE.primary}>
            Client
          </Typography>
          <FHAutocomplete
            data-testid="clientDropdownId"
            sx={{ flexGrow: 1, width: 320, boxShadow: 'none' }}
            inputHeight={40}
            coloredBackground
            placeholder="Select client"
            clearIcon={false}
            disabled={clientsDataLoading}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
              return (
                <Box
                  {...props}
                  sx={{
                    display: 'flex !important',
                    justifyContent: 'space-between !important',
                  }}
                  key={option.id}
                  data-testid={`clientLabel${option.id}`}
                >
                  <Typography>{option.label}</Typography>
                  {option.description && (
                    <Typography
                      sx={{
                        px: 0.7,
                        ml: 1,
                        borderRadius: '4px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: FORTA_WHITE,
                        backgroundColor: FORTA_GRAY4,
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              );
            }}
            options={clients.map((client: IClient) => ({
              id: client.id,
              label: `${client.first_name} ${client.last_name}`,
              description:
                client.clinical_status === ClinicalStatus.OFFBOARDED ||
                client.clinical_status === ClinicalStatus.ON_HOLD
                  ? client.clinical_status
                  : '',
            }))}
            value={selectedClient}
            onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
              setSelectedClient(newValue);
            }}
          />
        </Box>
        <Box textAlign="left" mb={3}>
          <Typography fontWeight={700} mb={1} color={FORTA_BLUE.primary}>
            Bill to
          </Typography>
          <FHSelect
            data-testid="payeeDropdownId"
            sx={{ flexGrow: 1, width: '320px', boxShadow: 'none', borderRadius: '8px' }}
            inputHeight={40}
            displayEmpty
            backgroundType={BackgroundTypes.Colored}
            popoverWidth={320}
            disabled={!selectedClient || providerAssignmentLoading}
            options={providerAssignments.map((pi) => ({
              value: pi.provider.id,
              label: `${pi.provider?.first_name} ${pi.provider?.last_name}`,
            }))}
            optionDataTestIdPrefix="payee_dropdown_option"
            value={selectedPayee}
            renderValue={selectedPayee ? undefined : () => <Typography>Select</Typography>}
            onUpdate={(value: SelectValueType) => {
              const selectedProvider = providerAssignments.find(
                (assignment) => String(assignment.provider.id) === String(value),
              );
              setSelectedPayee(selectedProvider?.provider.id ?? null);
            }}
            error={Boolean(selectedClient && !selectedPayee)}
          />
        </Box>
      </Box>

      {selectedClient && (
        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              p: 1,
              flexGrow: 1,
              borderRadius: '10px',
              boxShadow: '0px 2px 4px 2px rgba(0, 0, 0, 0.12)',
            }}
          >
            <FHMUIDataTable
              containerStyles={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                margin: '0 2px',
                padding: '16px',
                boxShadow: 'none',
                border: 'none',
                color: 'red',
              }}
              title=""
              data={transformedTableData}
              columns={getBuildInvoiceTableColumns(isSelectedAll, handleSelectAll)}
              options={getBuildInvoiceTableOptions(handleRowSelect)}
            />
          </Box>
          <SummaryBox
            totalDue={totalDue}
            selectedPayee={selectedPayee}
            totalNumClaimSelected={totalNumClaimSelected}
            isInvoicePreviewCreated={Boolean(invoicePreview)}
            dueInDays={dueInDays}
            clientName={selectedClient.label}
            discount={discount}
            setDiscount={setDiscount}
            onClick={handleCreateInvoicePreview}
          />
        </Box>
      )}
    </Box>
  );
}

export default InvoiceBuilder;
